import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `AnchoredPopover` — an overlay anchored to a trigger, rendered through a **body portal**
 * (parity card **P-005**).
 *
 * Vibe's `<Dialog>` renders its content *inline*, in a
 * `span.monday-style-dialog-content-wrapper` that is `position: absolute`, `z-index: auto`
 * and **transformed** — so it joins the stacking context of whatever opened it. Inside the
 * mobile sidebar drawer that is fatal: the workspace switcher's list painted *behind* the
 * drawer and no z-index on the list could lift it out, because the drawer is the parent
 * stacking context. `elementFromPoint` at the list's centre returned a tree-row button.
 *
 * This renders into `document.body` instead and takes its layer from the one scale
 * (`--ez-layer-popover`), so a popover opened from a drawer, a dialog or a sticky header is
 * always above it. It also:
 *
 *   - re-anchors on scroll and resize, and flips above the trigger when there is no room
 *     below (the same placement rule as the grid's `CellPopover`),
 *   - clamps to the viewport with an 8 px margin, so a 264 px menu on a 390 px phone is
 *     fully on screen rather than half of it,
 *   - closes on Escape and on a pointer-down outside both the popover and its trigger,
 *   - restores focus to the trigger when it closes.
 *
 * Round 7a (**P-007**, **P-008**) moved the arithmetic into `placement.ts`, which every overlay
 * primitive now shares, and added the two rules the sweep's 199 anchoring failures needed: the
 * popover caps its **width** at the viewport (a 440 px menu on a 390 px phone is narrowed, not
 * cropped), and a **degenerate anchor rectangle is not an anchor** — it keeps the last one the
 * trigger was known to have rather than clamping `(0, 0, 0, 0)` into the corner of the window.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayDismiss } from '../hooks/useOverlayDismiss.js';
import { OVERLAY_GAP, isUsableAnchor, placeAnchored } from './placement.js';
export function AnchoredPopover({ anchor, open, onClose, label, align = 'start', width, gap = OVERLAY_GAP, role = 'dialog', children, 'data-testid': dataTestId, }) {
    const ref = useRef(null);
    const [position, setPosition] = useState(null);
    /**
     * Only the **width** is written inline. `.ez-anchored-popover` already caps its height at
     * `min(70vh, 640px)` and scrolls, and an inline `max-height` would silently *raise* that cap —
     * a desktop geometry change nobody asked for. The width has no CSS cap, and a 440 px menu on a
     * 390 px phone is the half-off-screen defect P-008 reports.
     */
    const [maxWidth, setMaxWidth] = useState(null);
    /**
     * P-007. The trigger's rectangle is not always readable at the moment the popover places
     * itself: a `+` in a horizontally virtualised header, a button in a scroller that is being
     * scrolled, an anchor unmounted by the click that opened this. `getBoundingClientRect()` then
     * returns `(0, 0, 0, 0)`, the clamp's lower bound is the 8 px margin, and the popover paints in
     * the **top-left corner of the window** — which is how the add-column gallery opened at (8, 8)
     * on top of the sidebar while its `+` sat at x ≈ 1,420. A clamp that lands in the opposite
     * corner is worse than no clamp, so the last rectangle the anchor was known to have is kept and
     * used instead, and with no known rectangle at all the popover does not place itself.
     */
    const lastAnchorRect = useRef(null);
    const place = useCallback(() => {
        const live = anchor?.getBoundingClientRect();
        const box = isUsableAnchor(live) ? live : lastAnchorRect.current;
        if (!box)
            return;
        lastAnchorRect.current = { x: box.x, y: box.y, width: box.width, height: box.height };
        const own = ref.current?.getBoundingClientRect();
        const next = placeAnchored(box, { width: width ?? own?.width ?? 240, height: own?.height ?? 0 }, { width: window.innerWidth, height: window.innerHeight }, { align, gap });
        setPosition((current) => current && current.top === next.top && current.left === next.left ? current : { top: next.top, left: next.left });
        setMaxWidth((current) => (current === next.maxWidth ? current : next.maxWidth));
    }, [anchor, align, width, gap]);
    useLayoutEffect(() => {
        if (!open)
            return;
        place();
        // `true`: the anchor may be inside a scrolling drawer, whose scroll does not bubble.
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [open, place, children]);
    /**
     * **P-260.** Escape, and the return of focus to the trigger, now come from the one hook every
     * overlay in the product shares — with the anchor handed over explicitly, because this is the
     * one surface that already knows its trigger. It also puts the popover on the overlay stack,
     * so a menu opened *inside* it takes Escape first instead of both closing at once.
     *
     * `captureEscape` keeps the capture-phase listener this component has always used.
     */
    useOverlayDismiss({ open, onClose, opener: anchor, captureEscape: true });
    useEffect(() => {
        if (!open)
            return;
        const onPointerDown = (event) => {
            const target = event.target;
            if (ref.current?.contains(target))
                return;
            if (anchor?.contains(target))
                return;
            onClose();
        };
        document.addEventListener('mousedown', onPointerDown, true);
        return () => document.removeEventListener('mousedown', onPointerDown, true);
    }, [open, onClose, anchor]);
    if (!open || typeof document === 'undefined')
        return null;
    return createPortal(_jsx("div", { ref: ref, className: "ez-anchored-popover", role: role, "aria-label": label, "data-testid": dataTestId, 
        /*
         * Until the first measurement the popover is painted transparent rather than at (0, 0):
         * one frame in the corner of the window is exactly the defect P-007 reports, and a person
         * sees that frame.
         */
        style: {
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            visibility: position === null ? 'hidden' : undefined,
            ...(maxWidth === null ? undefined : { maxWidth }),
            ...(width === undefined ? undefined : { width }),
        }, children: children }), document.body);
}
//# sourceMappingURL=AnchoredPopover.js.map